import { SiteUrls } from '@tableau/shared-urls';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { GroupUsersGrid } from '../../app/content/GroupUsersGrid';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ISiteRoleFilter } from '../filters/SiteRoleFilter';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { GroupUserContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { SiteUsers } from '../resources/SiteUsers';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { GroupActions, GroupUsersEvent } from '../services/GroupActions';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { IGroupPlaceScope } from './GroupPlaceCtrl';

export interface IGroupUsersSubplaceScope extends
  IGroupPlaceScope,
  IActionsMenuScope<ISiteUserInfo, {}>,
  ListActionsScope,
  RefreshContentScope,
  SubplaceWithActionButtonsScope<ISiteUserInfo, {}>,
  SubplaceWithDataGridScope<ISiteUserInfo>
  {
  }

export class GroupUsersSubplaceCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'AngularToReactDataGridBridge',
    'siteRoleFilter',
    'ServerService',
    'BrowserTitleService',
    'SorterFactory',
    'SiteUsers',
    'QueryService',
    'GroupActions'
  ];

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'usersEmptyMessage' };

  constructor(
    private $scope: IGroupUsersSubplaceScope,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    private siteRoleFilter: ISiteRoleFilter,
    private ServerService: ServerService,
            BrowserTitleService: BrowserTitleService,
            SorterFactory: SorterFactory,
            users: SiteUsers,
            queryService: QueryService,
            groupActions: GroupActions
  ) {
    const group = $scope.group; // this group comes from the GroupSpaceProvider
    const selectedItems = new Set<ISiteUserInfo>(user => user.id);
    const actions = groupActions.getGroupUsersActionButtons(group.isAllUsersGroup);
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, selectedItems);

    this.configureForReactDataGrid(actions, getActionTarget);

    BrowserTitleService.setTitleWithSpaceEntityNameAndTranslatedSubspaceName({spaceEntityName: group.name, translatedSubspaceName: Messages.members()});

    // selection
    $scope.selectedItems = selectedItems;

    // query
    const queryUpdater: IQueryUpdater =
      queryService.createQueryUpdater(
        $scope,
        GroupUserContentType,
        users,
        (resourceQuery) => {
          this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: $scope, sliceable: resourceQuery });
          $scope.sliceable = resourceQuery;
        },
        { inGroup: group.id }
      );

    // refresh
    $scope.refreshContent = () => {
      queryUpdater.refresh();
    };

    $scope.refreshContent();

    $scope.$on(SubplaceEvents.Refresh, () => {
      $scope.refreshContent();
    });

    // sorting
    $scope.sorter = SorterFactory.create(GroupUserContentType.defaultOrder);
    $scope.$on('$destroy', () => $scope.sorter.detach());

    // actions
    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [{
      name: 'add-users-to-group',
      translate: 'addUsers',
      isAvailable: () => !group.isAllUsersGroup,
      execute: ($event: JQueryEventObject) => {
        groupActions.addUsersToGroup(group).then(() => {
          $scope.refreshContent();
          $scope.$emit(GroupUsersEvent.usersAdded);
        });
      }
    }];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.emptyTemplateModel = GroupUsersSubplaceCtrl.emptyTemplateModel;
  }

  private configureForReactDataGrid(actions: IActionButton<ISiteUserInfo, {}>[],
                                    getActionTarget: () => IActionTarget<ISiteUserInfo, {}>) {

    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    const columns = GroupUsersGrid.columns(
      (id: string) => this.$translate.instant(id),
      this.$scope,
      siteUrls,
      this.siteRoleFilter,
      actions,
      getActionTarget);

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(GroupUsersSubplaceCtrl.emptyTemplateModel),
      canShowFilterPanel: GroupUserContentType.canShowFilterPanel,
      headerHeight: CommonStyles.Sizes.DataGridWithPictureIconsHeaderHeight,
      rowOuterHeight: CommonStyles.Sizes.DataGridWithPictureIconsRowHeight
    });
  }
}

Module.instance().controller('GroupUsersSubplaceCtrl', GroupUsersSubplaceCtrl);
